import type { ReactNode } from "react";

export type TimeSeriesChartPoint = {
  label: string;
  value: number | null;
  sampleCount?: number;
};

export type TimeSeriesChartReference = {
  label: string;
  value: number;
};

export function timeSeriesAxisLabelIndexes(dataLength: number) {
  const labelStep = Math.max(1, Math.ceil(dataLength / 6));
  return Array.from(
    { length: dataLength },
    (_, index) => index,
  ).filter((index) => index % labelStep === 0);
}

export function TimeSeriesChart({
  ariaLabel,
  data,
  formatValue,
  reference,
  emptyState = "No weekly observations are available for this period.",
}: {
  ariaLabel: string;
  data: TimeSeriesChartPoint[];
  formatValue: (value: number) => string;
  reference?: TimeSeriesChartReference;
  emptyState?: ReactNode;
}) {
  const observed = data.filter(
    (point): point is TimeSeriesChartPoint & { value: number } =>
      point.value !== null && Number.isFinite(point.value),
  );
  if (!observed.length) {
    return <p className="ui-time-series-empty">{emptyState}</p>;
  }

  const width = 720;
  const height = 300;
  const left = 68;
  const right = 20;
  const top = 20;
  const bottom = 52;
  const plotWidth = width - left - right;
  const plotHeight = height - top - bottom;
  const validReference = reference && Number.isFinite(reference.value)
    ? reference
    : null;
  const maximum = Math.max(
    ...observed.map((point) => point.value),
    validReference?.value ?? 0,
    0,
  );
  const domainMaximum = maximum > 0 ? maximum : 1;
  const x = (index: number) =>
    data.length === 1
      ? left + plotWidth / 2
      : left + (index / (data.length - 1)) * plotWidth;
  const y = (value: number) => top + plotHeight - (value / domainMaximum) * plotHeight;
  const axisLabelIndexes = new Set(timeSeriesAxisLabelIndexes(data.length));
  const segments: Array<Array<{ x: number; y: number }>> = [];
  let segment: Array<{ x: number; y: number }> = [];
  data.forEach((point, index) => {
    if (point.value === null || !Number.isFinite(point.value)) {
      if (segment.length) {
        segments.push(segment);
        segment = [];
      }
      return;
    }
    segment.push({ x: x(index), y: y(point.value) });
  });
  if (segment.length) {
    segments.push(segment);
  }

  return (
    <figure className="ui-time-series-chart">
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={ariaLabel}>
        {[0, 0.5, 1].map((fraction) => {
          const value = domainMaximum * (1 - fraction);
          const position = top + plotHeight * fraction;
          return (
            <g key={fraction}>
              <line className="ui-time-series-grid" x1={left} x2={width - right} y1={position} y2={position} />
              <text className="ui-time-series-axis-label" x={left - 10} y={position + 4} textAnchor="end">
                {formatValue(value)}
              </text>
            </g>
          );
        })}
        {validReference && (
          <g>
            <line
              className="ui-time-series-reference"
              x1={left}
              x2={width - right}
              y1={y(validReference.value)}
              y2={y(validReference.value)}
            />
            <text
              className="ui-time-series-reference-label"
              x={width - right - 4}
              y={y(validReference.value) <= top + 12
                ? y(validReference.value) + 15
                : y(validReference.value) - 6}
              textAnchor="end"
            >
              {validReference.label} {formatValue(validReference.value)}
            </text>
          </g>
        )}
        {segments.map((points, index) => (
          <polyline
            className="ui-time-series-line"
            key={index}
            points={points.map((point) => `${point.x},${point.y}`).join(" ")}
          />
        ))}
        {data.map((point, index) => (
          <g key={`${point.label}:${index}`}>
            {point.value !== null && (
              <circle className="ui-time-series-point" cx={x(index)} cy={y(point.value)} r="4">
                <title>{point.label}: {formatValue(point.value)}{point.sampleCount !== undefined ? ` (${point.sampleCount} samples)` : ""}</title>
              </circle>
            )}
            {axisLabelIndexes.has(index) && (
              <text className="ui-time-series-axis-label" x={x(index)} y={height - 18} textAnchor="middle">
                {point.label}
              </text>
            )}
          </g>
        ))}
      </svg>
      <ul className="ui-visually-hidden">
        {validReference && (
          <li>{validReference.label}: {formatValue(validReference.value)}</li>
        )}
        {data.map((point, index) => (
          <li key={`${point.label}:${index}`}>
            {point.label}: {point.value === null ? "No observations" : formatValue(point.value)}
            {point.sampleCount !== undefined ? `, ${point.sampleCount} samples` : ""}
          </li>
        ))}
      </ul>
    </figure>
  );
}
